import Package from '../../package.json'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import {
  GetOffersRequest,
  GetOffersResponse,
  GetUserResponse,
  LookupUsersRequest,
  LookupUsersResponse,
  RedeemOfferRequest,
  RedeemOfferResponse,
  FinalizeCheckRequest,
  FinalizeCheckResponse,
  RemoveOfferRequest,
  RemoveOfferResponse,
  ValidateOfferRequest,
  ValidateOfferResponse,
} from './types'
import { order as squareOrderApi } from '@squareup/sq-addons-web-sdk'
import { sanitizeOrder } from 'utils/api'
import { RootState } from 'store/types'

type Api = ReturnType<typeof initializeApi>
let api: Api | null = null

const PRODUCTION_API_URL = 'https://loyalty.thanx.com/square'
const SANDBOX_API_URL = 'https://loyalty.thanxsandbox.com/square'

export function initializeApi() {
  const apiToken = process.env.SQ_AUTHORIZATION_TOKEN
  const createdApi = createApi({
    reducerPath: 'api',
    tagTypes: ['Offers'],
    baseQuery: async (args, api, extraOptions) => {
      const baseApiUrl =
        process.env.POS_ENV === 'sandbox' ? SANDBOX_API_URL : PRODUCTION_API_URL

      const orderAttrs = await (async () => {
        try {
          const config = (api.getState() as RootState).pos?.context
            ?.configurations

          const order = await squareOrderApi.retrieve()
          const locationId = (order.locationId || config?.locationId) as
            | string
            | undefined
          return {
            order_uid: order.id,
            location_uid: locationId,
          }
        } catch (_err) {
          // do nothing
        }

        return { order_uid: null, location_uid: null }
      })()

      const baseQuery = fetchBaseQuery({
        baseUrl: baseApiUrl,
        prepareHeaders: headers => {
          headers.set('Order-Id', orderAttrs.order_uid ?? '')
          headers.set('Location-Id', orderAttrs.location_uid ?? '')
          // this is for development; in production it'll get overwritten by
          // Square's proxy
          headers.set('Authorization', `Bearer ${apiToken}`)
          headers.set('Addon-Version', Package.version)

          return headers
        },
        fetchFn: (input, init) => {
          const customHeaders: Record<string, string> = {}
          const sdkEnv = process.env.SQ_ADDONS_SDK_ENVIRONMENT as string

          if (input instanceof Request) {
            input.headers.forEach((value, key) => {
              // TODO: Authorization header is supposed to get overwritten on the
              // server side but it's not working. Once Square fixes it, remove
              // this if check.
              if (
                sdkEnv === 'LOCALHOST' ||
                key.toLowerCase() !== 'authorization'
              ) {
                customHeaders[key] = value
              }
            })
          }

          return fetch(input, {
            ...init,
            headers: {
              ...init?.headers,
              ...customHeaders,
            },
          })
        },
      })

      return baseQuery(args, api, extraOptions)
    },
    endpoints: builder => ({
      lookupUsers: builder.query<LookupUsersResponse, LookupUsersRequest>({
        query: params => ({
          url: 'users/lookup',
          params: params,
        }),
      }),
      getUser: builder.query<GetUserResponse, string>({
        query: identifier => ({
          url: `users/${identifier}`,
        }),
      }),
      getOffers: builder.mutation<GetOffersResponse, GetOffersRequest>({
        query: ({ userIdentifier, order }) => ({
          url: `users/${userIdentifier}/offers`,
          method: 'POST',
          body: {
            order: sanitizeOrder(order),
          },
        }),
      }),
      redeemOffer: builder.mutation<RedeemOfferResponse, RedeemOfferRequest>({
        query: ({ userIdentifier, offerIdentifier, order }) => ({
          url: `users/${userIdentifier}/offers/${offerIdentifier}/redeem`,
          method: 'POST',
          body: {
            order: sanitizeOrder(order),
          },
        }),
      }),
      removeOffer: builder.mutation<RemoveOfferResponse, RemoveOfferRequest>({
        query: ({ userIdentifier, redemptionIdentifier }) => ({
          url: `users/${userIdentifier}/offers/${redemptionIdentifier}`,
          method: 'DELETE',
        }),
      }),
      validateOffer: builder.mutation<
        ValidateOfferResponse,
        ValidateOfferRequest
      >({
        query: ({ userIdentifier, order, redemptionIdentifier }) => ({
          url: `users/${userIdentifier}/offers/${redemptionIdentifier}/validate`,
          method: 'POST',
          body: {
            order: sanitizeOrder(order),
          },
        }),
      }),
      finalizeCheck: builder.mutation<
        FinalizeCheckResponse,
        FinalizeCheckRequest
      >({
        query: payload => ({
          url: 'purchases',
          method: 'POST',
          body: payload,
        }),
      }),
    }),
  })

  api = createdApi
  return createdApi
}

export function loyaltyApi(): Api {
  if (!api) {
    throw new Error(
      'Tried to use api before it was initialized. Call initializeApi first.'
    )
  }
  return api
}
